/**
 * Generated by orval v7.17.2 🍺
 * Do not edit manually.
 * Radius Proxy API
 * # OpenAPI Specification
This API is documented in [OpenAPI v3.1.0](https://swagger.io/specification/) format.

# Authentication
The API supports two authentication methods:

# Error Responses
Whenever an error appears (whether it's a notifier bug which leads to an internal server error,
or a simple validation error) the API response will be in the
[Problem Details](https://tools.ietf.org/html/rfc7807) format

For example, a 404 response body would look like:
```json
{
    "status": 404,
    "type": "problems/resource-not-found",
    "title": "Resource not found",
    "detail": "Some entity with ID 'sdsada' does not exist."
}
```

# Subscriber Operations

## New Subscriber
Creates authentication data and assigns a speed profile based on USOC-to-group-name mapping.

## Suspend Subscriber
Adds a disable entry for the user with possible values: `non-payment`, `abuse`.

## Remove Suspension
Removes the disable entry to restore service.

## Upgrade/Downgrade Subscriber
Updates the assigned speed profile.

## Manage Static IPs (Idempotent)
Add or remove IP addresses assigned to the subscriber.

## Cancel Subscriber
Complete subscriber cancellation that removes authentication data, speed profile association,
all static IPs, and disable entries.

 * OpenAPI spec version: 1.0
 */
import { useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  BadRequestResponse,
  InternalServerErrorResponse,
  SessionSearchResultsResponse,
  UISessionsSearchByIpParams,
  UnauthorizedResponse,
} from '.././model';

import { axiosInstance } from '.././axios-instance';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * Searches for BNG subscriber sessions that had the given IP address assigned
during the specified time window. Intended for legal/management queries such as
"who had IP X between time A and time B".

**IP Version Detection (automatic):**
The server detects the IP version automatically — no version flag required.

**IPv4 behaviour:**
Searches `framedipaddress` in `radacct` for an exact match.

**IPv6 behaviour:**
Performs two searches and merges the results:
1. Exact address match (`/128`) against `framedipv6address`
2. Calculated `/56` prefix match against `delegatedipv6prefix`

The `matched_by` field in each result indicates which column produced the match.
The `meta.searched_ipv6_prefix` field exposes the calculated prefix so the client
can display it without having to recompute it.

**Time-window intersection logic:**
A session is included when its interval **intersects** the requested `[from, to]`
window. Formally: `acctstarttimeе < to AND (acctstoptime IS NULL OR acctstoptime > from)`

This covers all intersection cases:
- Session started before the window and ended inside it
- Session started inside the window and ended after it
- Session is entirely within the window
- Session spans the entire window
- Still-active sessions (no stop time) that started before `to`

 * @summary Search Sessions by IP Address
 */
export const uISessionsSearchByIp = (
  params: UISessionsSearchByIpParams,
  options?: SecondParameter<typeof axiosInstance>,
  signal?: AbortSignal,
) => {
  return axiosInstance<SessionSearchResultsResponse>(
    { url: `/ui/sessions/search`, method: 'GET', params, signal },
    options,
  );
};

export const getUISessionsSearchByIpQueryKey = (params?: UISessionsSearchByIpParams) => {
  return [`/ui/sessions/search`, ...(params ? [params] : [])] as const;
};

export const getUISessionsSearchByIpQueryOptions = <
  TData = Awaited<ReturnType<typeof uISessionsSearchByIp>>,
  TError = BadRequestResponse | UnauthorizedResponse | InternalServerErrorResponse,
>(
  params: UISessionsSearchByIpParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uISessionsSearchByIp>>, TError, TData>>;
    request?: SecondParameter<typeof axiosInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUISessionsSearchByIpQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof uISessionsSearchByIp>>> = ({ signal }) =>
    uISessionsSearchByIp(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof uISessionsSearchByIp>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UISessionsSearchByIpQueryResult = NonNullable<Awaited<ReturnType<typeof uISessionsSearchByIp>>>;
export type UISessionsSearchByIpQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | InternalServerErrorResponse;

export function useUISessionsSearchByIp<
  TData = Awaited<ReturnType<typeof uISessionsSearchByIp>>,
  TError = BadRequestResponse | UnauthorizedResponse | InternalServerErrorResponse,
>(
  params: UISessionsSearchByIpParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof uISessionsSearchByIp>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof uISessionsSearchByIp>>,
          TError,
          Awaited<ReturnType<typeof uISessionsSearchByIp>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUISessionsSearchByIp<
  TData = Awaited<ReturnType<typeof uISessionsSearchByIp>>,
  TError = BadRequestResponse | UnauthorizedResponse | InternalServerErrorResponse,
>(
  params: UISessionsSearchByIpParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uISessionsSearchByIp>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof uISessionsSearchByIp>>,
          TError,
          Awaited<ReturnType<typeof uISessionsSearchByIp>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUISessionsSearchByIp<
  TData = Awaited<ReturnType<typeof uISessionsSearchByIp>>,
  TError = BadRequestResponse | UnauthorizedResponse | InternalServerErrorResponse,
>(
  params: UISessionsSearchByIpParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uISessionsSearchByIp>>, TError, TData>>;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Search Sessions by IP Address
 */

export function useUISessionsSearchByIp<
  TData = Awaited<ReturnType<typeof uISessionsSearchByIp>>,
  TError = BadRequestResponse | UnauthorizedResponse | InternalServerErrorResponse,
>(
  params: UISessionsSearchByIpParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof uISessionsSearchByIp>>, TError, TData>>;
    request?: SecondParameter<typeof axiosInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUISessionsSearchByIpQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
